import {clsx} from 'clsx'
import {AvatarStack, Link} from '@primer/react'
import {GitHubAvatar} from '@github-ui/github-avatar'
import {ProfileReference} from '@github-ui/profile-reference'
import {isCopilot} from '@github-ui/assignees/copilot-user'
import {useGetHovercardAttributesForType} from '@github-ui/hovercards/use-get-hovercard-attributes-for-type'

// Canonical app slug for the Copilot coding agent, used as a fallback when the backend
// has not supplied an explicit slug and the author is the Copilot agent (whose display
// login is "Copilot", not an app slug).
const COPILOT_SWE_AGENT_SLUG = 'copilot-swe-agent'

interface HeaderActor {
  login: string
  displayName?: string | null
  avatarUrl?: string | null
  slug?: string | null
}

export interface PullRequestHeaderAuthorsProps {
  agent: HeaderActor
  attributedUser: HeaderActor
  showAvatars?: boolean
  className?: string
}

/**
 * Renders "<attributed user> with <Copilot>", with their avatars stacked and the attributed user on top.
 */
export function PullRequestHeaderAuthors({
  agent,
  attributedUser,
  showAvatars = false,
  className,
}: PullRequestHeaderAuthorsProps) {
  const getHovercardAttributesForType = useGetHovercardAttributesForType()
  const agentSlug = agent.slug || (isCopilot(agent.login) ? COPILOT_SWE_AGENT_SLUG : agent.login)
  const userHovercardAttributes = getHovercardAttributesForType('user', {login: attributedUser.login})
  const agentHovercardAttributes = getHovercardAttributesForType('copilot', {botSlug: agentSlug})
  const agentDisplayName = agent.displayName || agent.login

  const renderAvatars = showAvatars && (attributedUser.avatarUrl || agent.avatarUrl)

  return (
    <span className={clsx('d-inline-flex flex-items-center', renderAvatars && 'gap-2', className)}>
      {renderAvatars ? (
        // disableExpand keeps the decorative avatars from adding a keyboard tab stop and from
        // expanding on hover/focus; the names below are the interactive, labelled targets.
        <AvatarStack disableExpand>
          {attributedUser.avatarUrl ? <GitHubAvatar src={attributedUser.avatarUrl} alt="" size={20} /> : null}
          {agent.avatarUrl ? <GitHubAvatar src={agent.avatarUrl} alt="" size={20} /> : null}
        </AvatarStack>
      ) : null}
      <span>
        <Link
          muted
          inline={!showAvatars}
          className="text-bold"
          href={`/${attributedUser.login}`}
          {...userHovercardAttributes}
        >
          <ProfileReference login={attributedUser.login} profileName={attributedUser.displayName} isAgent={false} />
        </Link>
        {' with '}
        <Link muted inline={!showAvatars} href={`/apps/${agentSlug}`} {...agentHovercardAttributes}>
          {agentDisplayName}
        </Link>
      </span>
    </span>
  )
}
